import React, { FC, useEffect, useMemo, useState } from 'react';
import { FormInstance, Radio, Skeleton, Tag, Tooltip } from 'antd';
import {
  AttachedTicketFeature,
  FeatureAvailability,
  LocalizedText,
  TicketFeature,
  TicketFeatureAssignmentInput,
} from 'apollo/types';
import useListTicketFeaturesQuery from 'apollo/resolvers/ticketFeatures/list';
import FeatureIcon from 'components/UI/FeatureIcon';
import * as texts from 'assets/texts/ticketFeatures';
import styles from './TicketFeaturesForm.module.scss';

type AttachedState = 'included' | 'availability' | 'excluded';

type FeatureState = AttachedState | 'absent';

type Selection = Record<string, { state: FeatureState; note?: LocalizedText | null }>;

const availabilityToState: Record<FeatureAvailability, AttachedState> = {
  [FeatureAvailability.Included]: 'included',
  [FeatureAvailability.AccordingToAvailability]: 'availability',
  [FeatureAvailability.NotIncluded]: 'excluded',
};

const stateToAvailability: Record<AttachedState, FeatureAvailability> = {
  included: FeatureAvailability.Included,
  availability: FeatureAvailability.AccordingToAvailability,
  excluded: FeatureAvailability.NotIncluded,
};

const buildSelection = (attached: AttachedTicketFeature[]): Selection =>
  attached.reduce<Selection>(
    (selection, feature) => ({
      ...selection,
      [feature.ticketFeatureId]: {
        state: availabilityToState[feature.availability],
        note: feature.note,
      },
    }),
    {}
  );

// The note round-trips untouched, but it arrives from the query carrying
// __typename, which the input type rejects.
const toNoteInput = (note?: LocalizedText | null) =>
  note ? { es: note.es, en: note.en ?? null } : undefined;

// Used until the catalogue query resolves: the attached rows already carry
// everything the input needs, so the form never holds the query shape.
const toFormValueFromAttached = (
  attached: AttachedTicketFeature[]
): TicketFeatureAssignmentInput[] =>
  [...attached]
    .sort((a, b) => a.position - b.position)
    .map((feature, index) => ({
      ticketFeatureId: feature.ticketFeatureId,
      availability: feature.availability,
      position: index,
      note: toNoteInput(feature.note),
    }));

const toFormValue = (
  catalogue: TicketFeature[],
  selection: Selection
): TicketFeatureAssignmentInput[] =>
  catalogue
    .filter((feature) => (selection[feature.id]?.state || 'absent') !== 'absent')
    .map((feature, index) => ({
      ticketFeatureId: feature.id,
      availability: stateToAvailability[selection[feature.id].state as AttachedState],
      position: index,
      note: toNoteInput(selection[feature.id].note),
    }));

type TicketFeaturesFormProps = {
  initialValue: AttachedTicketFeature[];
  variableName: string;
  formInstance?: FormInstance;
};

const TicketFeaturesForm: FC<TicketFeaturesFormProps> = ({
  initialValue,
  variableName,
  formInstance,
}: TicketFeaturesFormProps) => {
  const { data, loading } = useListTicketFeaturesQuery();
  const catalogue = data?.listTicketFeaturesBackOffice;

  const initialKey = initialValue
    .map((feature) => `${feature.ticketFeatureId}:${feature.position}`)
    .join('|');

  const [selection, setSelection] = useState<Selection>(() =>
    buildSelection(initialValue)
  );

  useEffect(() => {
    setSelection(buildSelection(initialValue));
  }, [initialKey]);

  // Attached features keep the order they were saved in; the rest follow in
  // catalogue order, so toggling one on appends it instead of reshuffling.
  const orderedCatalogue = useMemo(() => {
    if (!catalogue) return [];
    const savedPosition = new Map(
      initialValue.map((feature) => [feature.ticketFeatureId, feature.position])
    );
    return [...catalogue].sort(
      (a, b) =>
        (savedPosition.get(a.id) ?? Number.MAX_SAFE_INTEGER) -
        (savedPosition.get(b.id) ?? Number.MAX_SAFE_INTEGER)
    );
  }, [catalogue, initialKey]);

  // Writes on first render too, so an untouched form still submits an explicit
  // array — omitting the field would mean "leave untouched" and a cleared list
  // would never save.
  useEffect(() => {
    formInstance?.setFieldsValue({
      [variableName]: catalogue
        ? toFormValue(orderedCatalogue, selection)
        : toFormValueFromAttached(initialValue),
    });
  }, [catalogue, orderedCatalogue, selection]);

  const onStateChange = (id: string, state: FeatureState) => {
    setSelection((previous) => ({
      ...previous,
      [id]: { ...previous[id], state },
    }));
  };

  if (loading) return <Skeleton active paragraph={{ rows: 4 }} />;

  return (
    <div>
      <div className={styles.Panel}>
        {orderedCatalogue.map((feature) => (
          <div className={styles.Row} key={feature.id}>
            <span className={styles.Feature}>
              <FeatureIcon slug={feature.slug} description={feature.name.es} size={28} />
              <span className={styles.Name}>{feature.name.es}</span>
              {!feature.status && (
                <Tooltip title={texts.retiredTooltip}>
                  <Tag>{texts.retired}</Tag>
                </Tooltip>
              )}
            </span>
            <Radio.Group
              size="small"
              buttonStyle="solid"
              value={selection[feature.id]?.state || 'absent'}
              onChange={(event) =>
                onStateChange(feature.id, event.target.value as FeatureState)
              }
            >
              <Radio.Button value="included">{texts.included}</Radio.Button>
              <Tooltip title={texts.accordingToAvailabilityTooltip}>
                <Radio.Button value="availability" className={styles.Availability}>
                  {texts.accordingToAvailability}
                </Radio.Button>
              </Tooltip>
              <Radio.Button value="excluded">{texts.notIncluded}</Radio.Button>
              <Radio.Button value="absent">{texts.notMentioned}</Radio.Button>
            </Radio.Group>
          </div>
        ))}
      </div>
      <span className={styles.Legend}>{texts.legend}</span>
    </div>
  );
};

TicketFeaturesForm.defaultProps = {
  formInstance: undefined,
};

export default TicketFeaturesForm;
